/**
 * MobileExpedientes — listado de expedientes asignados al perito con
 * búsqueda rápida (nº siniestro, nº póliza, asegurado, dirección, NIF,
 * municipio, etc.).
 *
 * - Por defecto carga los 50 expedientes más recientes (el backend filtra
 *   por visibilidad del usuario, así que un perito sólo ve los suyos).
 * - El input dispara `GET /expedientes/_search?q=` con debounce de 300 ms
 *   cuando hay ≥2 caracteres.
 * - Cada item navega a `/m/expediente/{id}` (la vista detalle ya existe).
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import MobileShell from "@/mobile/MobileShell";
import GabineteChip from "@/components/GabineteChip";
import { useGabinetesMap } from "@/hooks/useGabinetesMap";
import {
  MagnifyingGlass, X, FolderSimple, MapPin, ArrowRight, Buildings,
} from "@phosphor-icons/react";

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("es-ES", {
  day: "2-digit", month: "2-digit", year: "2-digit",
}) : "—";

export default function MobileExpedientes() {
  const navigate = useNavigate();
  const { byId: gabineteById } = useGabinetesMap();
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);

  // Carga inicial: lista por defecto (recientes asignados)
  useEffect(() => {
    let cancel = false;
    if (q.trim().length >= 2) return undefined;
    setLoading(true);
    api.get("/expedientes", { params: { limit: 50 } })
      .then((r) => { if (!cancel) setItems(r.data || []); })
      .catch(() => { if (!cancel) setItems([]); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [q]);

  // Búsqueda con debounce
  useEffect(() => {
    if (q.trim().length < 2) return undefined;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/expedientes/_search", { params: { q: q.trim(), limit: 30 } });
        setItems(r.data || []);
      } catch {
        setItems([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const showLoading = loading || searching;

  return (
    <MobileShell title="Mis expedientes">
      {/* Buscador sticky */}
      <div className="sticky top-12 z-10 bg-white border-b border-[#E8EAED] px-3 py-2.5">
        <div className="relative">
          <MagnifyingGlass className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"
                            weight="bold" />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar siniestro, póliza, asegurado, dirección…"
            data-testid="mobile-expedientes-search"
            className="w-full !pl-9 !pr-9 !text-[13px] !h-10"
            inputMode="search"
            autoCorrect="off"
            autoCapitalize="none"
          />
          {q && (
            <button onClick={() => setQ("")}
                    data-testid="mobile-expedientes-search-clear"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 p-1 rounded hover:bg-zinc-100">
              <X className="w-3.5 h-3.5" weight="bold" />
            </button>
          )}
        </div>
        <div className="mt-1 text-[10.5px] text-muted">
          {showLoading
            ? "Buscando…"
            : (q ? `${items.length} resultado${items.length === 1 ? "" : "s"}`
                  : `${items.length} expediente${items.length === 1 ? "" : "s"} reciente${items.length === 1 ? "" : "s"}`)}
        </div>
      </div>

      <div className="px-3 pt-3 space-y-2" data-testid="mobile-expedientes-list">
        {!showLoading && items.length === 0 && (
          <div className="bg-white rounded-md border border-[#E8EAED] p-8 text-center">
            <FolderSimple className="w-8 h-8 mx-auto text-muted mb-2" weight="duotone" />
            <div className="text-[14px] font-semibold">
              {q ? "Sin coincidencias" : "Aún no tienes expedientes asignados"}
            </div>
            <div className="text-[12px] text-muted mt-1">
              {q ? "Prueba con otra palabra (nº siniestro, NIF, dirección…)" : "Cuando se te asigne uno aparecerá aquí."}
            </div>
          </div>
        )}
        {items.map((e) => (
          <ExpedienteRow key={e.id} exp={e}
                          gabinete={gabineteById(e.gabinete_id)}
                          onOpen={() => navigate(`/m/expediente/${encodeURIComponent(e.id)}`)} />
        ))}
      </div>
    </MobileShell>
  );
}

function ExpedienteRow({ exp, gabinete, onOpen }) {
  const data = exp.extracted_data || {};
  const numeroSin = data.numero_siniestro_gabinete || data.numero_siniestro || exp.dobilas_expediente_id;
  const aseg = data.aseguradora || exp.detected_aseguradora;
  const asegurado = data.asegurado_nombre || "—";
  const direccion = [data.direccion_riesgo, data.municipio].filter(Boolean).join(", ");
  const estado = (exp.estado_encargo || exp.status || "").replace(/_/g, " ");

  return (
    <button
      onClick={onOpen}
      data-testid={`mobile-exp-${exp.id}`}
      className="w-full text-left bg-white rounded-md border border-[#E8EAED] p-3 hover:border-amber-300 active:translate-y-px transition"
    >
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 text-[11px] text-muted">
            {numeroSin && <span className="font-semibold text-foreground">Nº {numeroSin}</span>}
            {aseg && (
              <>
                <span>·</span>
                <span className="inline-flex items-center gap-0.5">
                  <Buildings className="w-3 h-3" weight="fill" /> {aseg}
                </span>
                <GabineteChip gabinete={gabinete} size="xs" className="ml-0.5" />
              </>
            )}
          </div>
          <div className="font-semibold text-[14px] truncate mt-0.5">{asegurado}</div>
          {direccion && (
            <div className="text-[12px] text-zinc-700 mt-0.5 line-clamp-2 flex items-start gap-1">
              <MapPin className="w-3 h-3 flex-shrink-0 mt-0.5 text-zinc-400" weight="fill" />
              <span>{direccion}</span>
            </div>
          )}
          <div className="flex items-center gap-2 mt-1.5 text-[10.5px]">
            {estado && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200 capitalize">
                {estado}
              </span>
            )}
            <span className="text-muted">Abierto {fmtDate(exp.created_at)}</span>
          </div>
        </div>
        <ArrowRight className="w-4 h-4 text-zinc-400 flex-shrink-0 mt-1" weight="bold" />
      </div>
    </button>
  );
}
